import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StanDropdown } from '@/components';
import { useUser } from '@/queries/useUser';
import { stanNotify } from '@/lib/stanNotify';
import { useStoreSettings } from '../../queries/useStoreSettings';
import { LANGUAGE_LIST } from '../../constants/store';

export const LocalizationSection: React.FC = () => {
    const { user } = useUser();
    const [language, setLanguage] = useState('en');
    const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const { mutateAsync: updateStoreSettingsMutation, isPending: loading } = useStoreSettings();

    // Initialize from user data
    useEffect(() => {
        if (user?.data.language) {
            setLanguage(user.data.language || 'en');
        }
    }, [user?.data.language]);

    const handleLanguageChange = useCallback(
        async (value: string) => {
            setLanguage(value);
            setSaveState('saving');

            try {
                await updateStoreSettingsMutation({ language: value });

                setSaveState('saved');

                setTimeout(() => {
                    setSaveState('idle');
                }, 2000);

                stanNotify({
                    type: 'success',
                    title: 'Success!',
                    text: 'Language has been saved.',
                });
            } catch (error: any) {
                console.error('Settings update error:', error);
                setSaveState('error');

                stanNotify({
                    type: 'error',
                    title: 'Something went wrong',
                    text: error?.response?.data?.message || 'Please try again later.',
                });
            }
        },
        [updateStoreSettingsMutation]
    );

    const languageOptions = useMemo(
        () =>
            LANGUAGE_LIST.map(lang => ({
                label: lang.name,
                value: lang.value,
            })),
        []
    );

    const currentLanguageValue = useMemo(
        () => ({
            label: LANGUAGE_LIST.find(l => l.value === language)?.name || language,
            value: language,
        }),
        [language]
    );

    const handleSelect = useCallback(
        (value: { label: string; value: string }) => {
            handleLanguageChange(value.value);
        },
        [handleLanguageChange]
    );

    return (
        <div className='rounded-lg'>
            <div className='mb-2'>
                <h4>Change Store Checkout Language</h4>
                <p className='text-caption-1-regular text-gray mt-2'>
                    Set the language customers will see on the checkout page from your storefront.
                </p>
            </div>
            <div className='flex items-center gap-4'>
                <div className='max-w-xs flex-1'>
                    <StanDropdown
                        key={language}
                        id='language-select'
                        name='language'
                        options={languageOptions}
                        onSelect={handleSelect}
                        currentValue={currentLanguageValue}
                        placeholder='Select language'
                        disabled={loading}
                    />
                </div>
                {saveState === 'saving' && (
                    <div className='text-caption-1-regular text-gray flex items-center'>
                        <div className='mr-2 size-4 animate-spin rounded-full border-b-2' />
                        Saving...
                    </div>
                )}
                {saveState === 'saved' && <span className='text-caption-1-regular text-gray'>Saved!</span>}
            </div>
        </div>
    );
};
